import { Box } from "@mui/material";

/**
 * Guest detail side art — travel + guest themed.
 * Columns use flex + space-evenly + top/bottom stretch so icons fill the full section height.
 */

const accent = "rgba(0, 113, 194, 0.58)";
const accentSoft = "rgba(0, 113, 194, 0.24)";
const primarySoft = "rgba(27, 60, 115, 0.34)";

const mini = {
  display: "block",
  width: "100%",
  height: "auto",
  maxHeight: 52,
};

const columnSx = (side) => ({
  display: { xs: "none", sm: "flex" },
  position: "absolute",
  [side === "left" ? "left" : "right"]: { sm: 2, md: 6, lg: 10 },
  top: 0,
  bottom: 0,
  width: { sm: 56, md: 68, lg: 76 },
  maxWidth: { sm: "14%", md: "12%", lg: "11%" },
  flexDirection: "column",
  justifyContent: "space-evenly",
  alignItems: "center",
  py: { sm: 0.5, md: 1 },
  zIndex: 0,
  pointerEvents: "none",
  opacity: 0.95,
  ...(side === "left"
    ? { borderLeft: "2px dashed rgba(0, 113, 194, 0.14)", pl: 0.5 }
    : { borderRight: "2px dashed rgba(0, 113, 194, 0.14)", pr: 0.5 }),
});

const itemWrap = {
  width: "100%",
  maxWidth: 76,
  flexShrink: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

/* --- Travel / journey --- */

function IconPlane() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <path
        d="M12 48 L44 40 L56 28 L64 30 L56 44 L84 48 L56 52 L48 66 L40 64 L44 52 Z"
        fill={accentSoft}
        stroke={accent}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="72" cy="24" r="10" fill={accentSoft} opacity={0.7} />
    </svg>
  );
}

function IconPassport() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="26" y="14" width="44" height="68" rx="6" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <circle cx="48" cy="40" r="10" fill="none" stroke={accent} strokeWidth="1.2" />
      <path d="M40 38 L44 42 L54 34" fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="32" y="54" width="32" height="3" rx="1" fill={primarySoft} />
      <rect x="32" y="60" width="22" height="2" rx="1" fill={primarySoft} opacity={0.7} />
    </svg>
  );
}

function IconMap() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <path
        d="M22 22 L46 18 L72 24 L76 22 L76 74 L52 78 L28 72 L22 76 Z"
        fill={accentSoft}
        stroke={accent}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M38 34 L58 40 L48 58 Z" fill={primarySoft} opacity={0.5} />
      <path d="M52 32 L52 44 L48 44 L52 54 L58 44 L54 44 Z" fill={accent} />
    </svg>
  );
}

function IconHotel() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <path
        d="M22 72 L22 38 L48 22 L74 38 L74 72 Z"
        fill={accentSoft}
        stroke={accent}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <rect x="34" y="48" width="8" height="10" fill={primarySoft} />
      <rect x="54" y="48" width="8" height="10" fill={primarySoft} />
      <rect x="42" y="62" width="12" height="10" rx="1" fill={accent} opacity={0.45} />
      <path d="M38 38 L48 30 L58 38" fill="none" stroke={accent} strokeWidth="1.5" />
    </svg>
  );
}

function IconTrain() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="20" y="32" width="56" height="36" rx="6" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <rect x="28" y="40" width="14" height="12" rx="2" fill={primarySoft} />
      <rect x="54" y="40" width="14" height="12" rx="2" fill={primarySoft} />
      <rect x="38" y="54" width="20" height="6" rx="2" fill={accent} opacity={0.4} />
      <circle cx="32" cy="74" r="5" fill={primarySoft} />
      <circle cx="64" cy="74" r="5" fill={primarySoft} />
      <path d="M48 24 L48 32" stroke={accent} strokeWidth="2" />
    </svg>
  );
}

function IconGlobe() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <circle cx="48" cy="48" r="28" fill="none" stroke={accent} strokeWidth="1.5" opacity={0.7} />
      <ellipse cx="48" cy="48" rx="28" ry="12" fill="none" stroke={accentSoft} strokeWidth="1.2" />
      <path d="M20 48 H76 M48 20 V76" stroke={accentSoft} strokeWidth="1" opacity={0.6} />
    </svg>
  );
}

function IconCompass() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <circle cx="48" cy="48" r="30" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <path d="M48 22 L52 46 L48 48 L44 46 Z" fill={accent} />
      <path d="M74 48 L50 46 L48 48 L50 52 Z" fill={primarySoft} />
      <circle cx="48" cy="48" r="5" fill="#fff" stroke={accent} strokeWidth="1" />
    </svg>
  );
}

function IconBackpack() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="32" y="36" width="32" height="38" rx="6" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <path d="M40 36 Q48 28 56 36" fill="none" stroke={primarySoft} strokeWidth="4" strokeLinecap="round" />
      <rect x="38" y="48" width="20" height="4" rx="1" fill={accent} opacity={0.35} />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="22" y="28" width="52" height="48" rx="4" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <path d="M22 40 H74" stroke={accent} strokeWidth="1.2" />
      <path d="M34 22 V34 M62 22 V34" stroke={accent} strokeWidth="2" strokeLinecap="round" />
      <circle cx="36" cy="54" r="4" fill={accent} opacity={0.4} />
      <circle cx="48" cy="54" r="4" fill={primarySoft} />
      <circle cx="60" cy="54" r="4" fill={accent} opacity={0.35} />
    </svg>
  );
}

/* --- Guest / people --- */

function IconGuestId() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="20" y="24" width="56" height="52" rx="6" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <circle cx="48" cy="42" r="10" fill={primarySoft} />
      <path d="M32 64 Q48 54 64 64" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" />
      <rect x="58" y="30" width="12" height="8" rx="1" fill={accent} opacity={0.35} />
    </svg>
  );
}

function IconGuests() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <circle cx="38" cy="36" r="9" fill={primarySoft} />
      <circle cx="58" cy="36" r="9" fill={accentSoft} stroke={accent} strokeWidth="1" />
      <path d="M26 68 Q38 52 50 60" fill="none" stroke={accent} strokeWidth="2" />
      <path d="M46 60 Q58 52 70 68" fill="none" stroke={accent} strokeWidth="2" />
    </svg>
  );
}

function IconHeartTrip() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <path
        d="M48 78 C32 62 18 50 24 38 C28 30 40 28 48 36 C56 28 68 30 72 38 C78 50 64 62 48 78 Z"
        fill={accentSoft}
        stroke={accent}
        strokeWidth="1.2"
      />
      <path d="M28 36 L68 20" stroke={accent} strokeWidth="1.5" strokeDasharray="3 4" />
      <path d="M72 24 L68 20 L70 26" fill="none" stroke={accent} strokeWidth="1.2" />
    </svg>
  );
}

/* --- Right column extras --- */

function IconBoardingPass() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="16" y="30" width="64" height="40" rx="6" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <path d="M52 30 V70" stroke={accent} strokeWidth="1" strokeDasharray="4 3" opacity={0.5} />
      <rect x="22" y="38" width="24" height="3" rx="1" fill={primarySoft} />
      <rect x="22" y="46" width="20" height="2" rx="1" fill={primarySoft} opacity={0.7} />
      <circle cx="68" cy="50" r="8" fill={accent} opacity={0.4} />
    </svg>
  );
}

function IconCamera() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="22" y="34" width="52" height="38" rx="6" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <rect x="30" y="28" width="16" height="8" rx="2" fill={primarySoft} />
      <circle cx="48" cy="52" r="14" fill="#fff" stroke={accent} strokeWidth="1.5" />
      <circle cx="48" cy="52" r="7" fill={primarySoft} opacity={0.4} />
    </svg>
  );
}

function IconSuitcase() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="28" y="40" width="40" height="32" rx="5" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <path d="M36 40 V32 Q48 26 60 32 V40" fill="none" stroke={primarySoft} strokeWidth="3" />
      <rect x="34" y="50" width="28" height="4" rx="1" fill={accent} opacity={0.35} />
      <circle cx="36" cy="76" r="4" fill={primarySoft} />
      <circle cx="60" cy="76" r="4" fill={primarySoft} />
    </svg>
  );
}

function IconDrink() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <path d="M36 28 L40 72 H56 L60 28 Z" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <path d="M36 28 L48 18 L60 28" fill="none" stroke={accent} strokeWidth="1.2" />
      <ellipse cx="54" cy="26" rx="10" ry="3" fill={accent} opacity={0.4} transform="rotate(-15 54 26)" />
    </svg>
  );
}

function IconSunCloud() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <circle cx="66" cy="34" r="12" fill={accentSoft} stroke={accent} strokeWidth="1" />
      <ellipse cx="40" cy="58" rx="28" ry="14" fill="#fff" stroke={accentSoft} strokeWidth="1.2" />
      <ellipse cx="48" cy="54" rx="16" ry="9" fill={accentSoft} opacity={0.45} />
    </svg>
  );
}

function IconTaxi() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <rect x="18" y="40" width="60" height="24" rx="4" fill={accentSoft} stroke={accent} strokeWidth="1.2" />
      <path d="M28 40 L36 28 H60 L68 40" fill={primarySoft} opacity={0.5} />
      <rect x="38" y="44" width="20" height="10" rx="2" fill={primarySoft} opacity={0.4} />
      <circle cx="30" cy="68" r="6" fill={primarySoft} />
      <circle cx="66" cy="68" r="6" fill={primarySoft} />
    </svg>
  );
}

function IconUmbrella() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <path
        d="M48 22 Q72 22 78 42 Q48 36 18 42 Q24 22 48 22 Z"
        fill={accentSoft}
        stroke={accent}
        strokeWidth="1.2"
      />
      <path d="M48 42 V78" stroke={accent} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconKey() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <circle cx="38" cy="44" r="14" fill="none" stroke={accent} strokeWidth="3" />
      <path d="M48 52 L68 72 H74 V78" stroke={accent} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M68 72 V66 M74 72 V66" stroke={accent} strokeWidth="2" />
    </svg>
  );
}

function IconStars() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" style={mini} aria-hidden>
      <path
        d="M48 16 L52 32 L68 36 L52 40 L48 56 L44 40 L28 36 L44 32 Z"
        fill={accent}
        opacity={0.4}
      />
      <path d="M24 56 L26 62 L32 64 L26 66 L24 72 L22 66 L16 64 L22 62 Z" fill={accentSoft} />
      <path d="M72 68 L74 72 L78 73 L74 74 L72 78 L70 74 L66 73 L70 72 Z" fill={primarySoft} opacity={0.7} />
    </svg>
  );
}

/** Left: journey + guest motifs, evenly spaced full height */
export function GuestDecorLeftColumn() {
  const icons = [
    IconPlane,
    IconPassport,
    IconMap,
    IconHotel,
    IconGuestId,
    IconTrain,
    IconGlobe,
    IconBackpack,
    IconCompass,
    IconCalendar,
    IconGuests,
    IconHeartTrip,
  ];
  return (
    <Box aria-hidden sx={columnSx("left")}>
      {icons.map((IconCmp, index) => (
        <Box key={index} sx={itemWrap}>
          <IconCmp />
        </Box>
      ))}
    </Box>
  );
}

/** Right: tickets, bags, resort — evenly spaced full height */
export function GuestDecorRightColumn() {
  const icons = [
    IconBoardingPass,
    IconCamera,
    IconSuitcase,
    IconDrink,
    IconSunCloud,
    IconTaxi,
    IconUmbrella,
    IconKey,
    IconGlobe,
    IconStars,
    IconHeartTrip,
    IconPlane,
  ];
  return (
    <Box aria-hidden sx={columnSx("right")}>
      {icons.map((IconCmp, index) => (
        <Box key={index} sx={itemWrap}>
          <IconCmp />
        </Box>
      ))}
    </Box>
  );
}

/** Kept for backwards compatibility if imported elsewhere */
export function TravelDecorLeft() {
  return <IconPlane />;
}
export function TravelDecorRight() {
  return <IconBoardingPass />;
}
